import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Star, MoreHorizontal, Trash2, Users, UserCheck } from "lucide-react";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { gradientFor } from "@/lib/boardColors";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function BoardCard({ board }) {
  const qc = useQueryClient();
  const { t } = useTranslation();

  const star = useMutation({
    mutationFn: async () => (await api.patch(`/boards/${board.board_id}`, { is_starred: !board.is_starred })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recent-boards"] });
      qc.invalidateQueries({ queryKey: ["boards"] });
    },
  });

  const del = useMutation({
    mutationFn: async () => (await api.delete(`/boards/${board.board_id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recent-boards"] });
      qc.invalidateQueries({ queryKey: ["boards"] });
      toast.success(t("boardCard.deleted"));
    },
    onError: () => toast.error(t("boardCard.deleteFail")),
  });

  return (
    <div
      data-testid={`board-card-${board.board_id}`}
      className="relative group rounded-2xl overflow-hidden glass-card hover:-translate-y-0.5 hover:shadow-xl transition"
    >
      <Link to={`/boards/${board.board_id}`} className="block">
        <div className={cn("h-24 bg-gradient-to-br", gradientFor(board.cover_color))}>
          {board.relation === "shared_with_me" && (
            <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider font-semibold bg-black/30 text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`board-card-shared-${board.board_id}`}>
              <UserCheck className="h-3 w-3" /> {t("boardCard.sharedBadge")}
            </span>
          )}
          {board.relation === "collaborative" && (
            <span className="absolute top-2 left-2 text-[10px] uppercase tracking-wider font-semibold bg-black/30 text-white px-2 py-0.5 rounded-full inline-flex items-center gap-1" data-testid={`board-card-collab-${board.board_id}`}>
              <Users className="h-3 w-3" /> {t("boardCard.collaborativeBadge")}
            </span>
          )}
        </div>
        <div className="p-4">
          <p className="font-display text-base font-semibold tracking-tight truncate">{board.name}</p>
          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{board.description || t("boardCard.tableroFallback")}</p>
        </div>
      </Link>

      <button
        onClick={(e) => { e.preventDefault(); star.mutate(); }}
        data-testid={`board-star-${board.board_id}`}
        className="absolute top-2 right-10 h-8 w-8 rounded-full bg-black/20 hover:bg-black/30 grid place-items-center opacity-0 group-hover:opacity-100 transition"
        aria-label={t("boardCard.starAria")}
      >
        <Star className={cn("h-4 w-4", board.is_starred ? "text-amber-300 fill-amber-300" : "text-white")} />
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={`board-menu-${board.board_id}`}
            onClick={(e) => e.preventDefault()}
            className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/20 hover:bg-black/30 grid place-items-center opacity-0 group-hover:opacity-100 transition"
          >
            <MoreHorizontal className="h-4 w-4 text-white" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="glass-panel">
          <DropdownMenuItem
            onClick={() => {
              if (window.confirm(t("boardCard.deleteConfirm"))) del.mutate();
            }}
            className="text-rose-500"
            data-testid={`board-delete-${board.board_id}`}
          >
            <Trash2 className="h-4 w-4 mr-2" /> {t("boardCard.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
